/* global console */
import type React from "react";
import type { ReviewTileItem, SearchResult } from "@/types/tile.types";
import { findBestFuzzyMatch } from "./tile-search-score.utils";
import { executeSearchTiles, UseTileSearchOptions } from "./tile-search-execute.utils";
import { DOCUMENT_SEARCH, locateAndSelectInDocument } from "./locate-text";
import { locateMissMessage } from "./locate-text.policy";

export function executeSearchByField(
  tiles: ReviewTileItem[],
  field: keyof ReviewTileItem,
  searchTerm: string,
  findMatches: (text: string, term: string) => { startIndex: number; endIndex: number }[]
): SearchResult[] {
  const results: SearchResult[] = [];
  for (const tile of tiles) {
    const fv = tile[field];
    if (typeof fv === "string" && fv) {
      const matches = findMatches(fv, searchTerm);
      if (matches.length > 0) {
        results.push({
          tile,
          matches: [
            {
              field,
              text: fv,
              startIndex: matches[0]?.startIndex ?? 0,
              endIndex: matches[0]?.endIndex ?? 0,
            },
          ],
          score: matches.length,
        });
      }
    }
  }
  results.sort((a, b) => b.score - a.score);
  return results;
}

export function executeFuzzySearch(
  tiles: ReviewTileItem[],
  searchTerm: string,
  normalizeText: (text: string) => string
): SearchResult[] {
  const minScore = 0.6;
  const results: SearchResult[] = [];
  for (const tile of tiles) {
    const { bestScore, bestMatches } = findBestFuzzyMatch(
      tile,
      searchTerm,
      normalizeText,
      minScore
    );
    if (bestScore >= minScore) results.push({ tile, matches: bestMatches, score: bestScore * 100 });
  }
  results.sort((a, b) => b.score - a.score);
  return results;
}

function tileSearchOptions(options?: UseTileSearchOptions) {
  return {
    ...DOCUMENT_SEARCH,
    matchCase: options?.caseSensitive ?? false,
    matchWholeWord: options?.wholeWords ?? false,
  };
}

export async function executeSearchInDocumentWithTile(
  item: ReviewTileItem,
  fuzzySearchInDocument: (text: string) => Promise<boolean>,
  getSearchableText: (item: ReviewTileItem) => string | undefined,
  normalizeSearchText: (text: string) => string,
  options?: UseTileSearchOptions
): Promise<boolean> {
  try {
    const searchableText = getSearchableText(item);
    if (searchableText == null || searchableText === "") {
      options?.onError?.("No searchable text found for this tile");
      return false;
    }
    const normalized = normalizeSearchText(searchableText);
    if (normalized === "") {
      options?.onError?.("No valid search text after normalization");
      return false;
    }
    const located = await locateAndSelectInDocument(normalized, tileSearchOptions(options));
    if (located.found) {
      options?.onSuccess?.(
        located.via === "delimiter"
          ? "Found 1 match in document"
          : `Found ${located.count} matches in document`
      );
      return true;
    }
    const fuzzyResult = await fuzzySearchInDocument(normalized);
    if (fuzzyResult) {
      options?.onSuccess?.("Found match using fuzzy search");
      return true;
    }
    options?.onError?.(locateMissMessage("find", located.reason));
    return false;
  } catch (error: unknown) {
    console.error("Error in searchInDocumentWithTile:", error);
    const msg = error instanceof Error ? error.message : "Unknown error";
    options?.onError?.(`Search failed: ${msg}`);
    return false;
  }
}

export function wrapWithSearchingState<T extends unknown[]>(
  setIsSearching: (v: boolean) => void,
  fn: (...args: T) => Promise<SearchResult[]>
): (...args: T) => Promise<SearchResult[]> {
  return async (...args: T) => {
    setIsSearching(true);
    try {
      return await fn(...args);
    } finally {
      setIsSearching(false);
    }
  };
}

export function createSearchTilesCallback(
  findMatches: (text: string, term: string) => { startIndex: number; endIndex: number }[],
  normalizeText: (text: string) => string,
  setSearchResults: (results: SearchResult[]) => void,
  setCurrentSearchTerm: (term: string) => void,
  setSearchHistory: React.Dispatch<React.SetStateAction<string[]>>,
  options?: UseTileSearchOptions
): (tiles: ReviewTileItem[], searchTerm: string) => Promise<SearchResult[]> {
  return async (tiles, searchTerm) => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      return [];
    }
    setCurrentSearchTerm(searchTerm);
    setSearchHistory((prev) => [searchTerm, ...prev.filter((t) => t !== searchTerm)].slice(0, 10));
    const results = await executeSearchTiles(tiles, searchTerm, findMatches, normalizeText);
    setSearchResults(results);
    options?.onSearchComplete?.(results);
    options?.onSuccess?.(`Found ${results.length} tiles matching "${searchTerm}"`);
    return results;
  };
}

export function createSearchByFieldCallback(
  findMatches: (text: string, term: string) => { startIndex: number; endIndex: number }[],
  setSearchResults: (results: SearchResult[]) => void,
  options?: UseTileSearchOptions
): (
  tiles: ReviewTileItem[],
  field: keyof ReviewTileItem,
  searchTerm: string
) => Promise<SearchResult[]> {
  return async (tiles: ReviewTileItem[], field: keyof ReviewTileItem, searchTerm: string) => {
    const results = executeSearchByField(tiles, field, searchTerm, findMatches);
    setSearchResults(results);
    options?.onSuccess?.(`Found ${results.length} tiles with "${searchTerm}" in ${field}`);
    return results;
  };
}

export function createFuzzySearchCallback(
  normalizeText: (text: string) => string,
  setSearchResults: (results: SearchResult[]) => void,
  options?: UseTileSearchOptions
): (tiles: ReviewTileItem[], searchTerm: string) => Promise<SearchResult[]> {
  return async (tiles: ReviewTileItem[], searchTerm: string) => {
    const results = executeFuzzySearch(tiles, searchTerm, normalizeText);
    setSearchResults(results);
    options?.onSuccess?.(`Fuzzy search found ${results.length} potential matches`);
    return results;
  };
}
